import {
  Box,
  Group,
  LoadingOverlay,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  isBulkCreationSupportedForProductType,
  useOrderWorkflow,
  useV2ProductTypes,
} from "@jasper/product-workflows";
import {
  CreateProductArgs,
  hasGraphQLErrorCode,
  useOrderConfiguration,
} from "@jasper/use-cases";
import { CategoryPanel } from "./product/CategoryPanel";
import { filterCatalog } from "./product/filterCatalog";
import {
  findProductDuplicatedByDefaults,
  getCategoryDescription,
  PRODUCT_CATEGORIES,
  ProductChoice,
} from "./product/product";
import { ProductCategoryCard } from "./product/ProductCategory";
import { ProductSearch } from "./product/ProductSearch";
import { SearchPanel } from "./product/SearchPanel";
import { ProductTypeV2 } from "/@/generated/graphql";

export const AddProduct = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { isV2Product } = useV2ProductTypes();

  const { state } = useLocation() as {
    state?: { productType?: string; teeth?: number[] };
  };
  const autoCreated = useRef(false);

  const [selectedType, setSelectedType] = useState<ProductTypeV2 | null>(null);
  const [selectedTeeth, setSelectedTeeth] = useState<number[]>([]);

  const { order, addOrderProducts, isAddingProducts } = useOrderConfiguration({
    orderId: orderId!,
  });

  // Coming from the V1 funnel: type + teeth were already selected there,
  // create the product directly and skip the selection step.
  useEffect(() => {
    const presetType = state?.productType as ProductTypeV2 | undefined;
    if (
      autoCreated.current ||
      !presetType ||
      !isV2Product(presetType) ||
      !state?.teeth?.length
    ) {
      return;
    }
    autoCreated.current = true;
    // reflect the preset in the UI in case creation fails (error stays visible)
    setSelectedType(presetType);

    setSelectedTeeth(state.teeth);
    // consume the state so back-navigation doesn't re-create
    window.history.replaceState({}, "");
    void createProduct({
      type: presetType,
      teethToUse: state.teeth,
      v1Adapter: true,
    });
  }, [state, isV2Product]);

  const { goNext } = useOrderWorkflow({ orderId: orderId ?? "" });

  const createProduct = async ({
    type,
    teethToUse,
    v1Adapter,
    defaultProducts,
  }: {
    type: ProductTypeV2;
    teethToUse: number[];
    v1Adapter?: boolean;
    defaultProducts?: CreateProductArgs[];
  }) => {
    if (!orderId) return;

    const isBulkCreation =
      v1Adapter &&
      teethToUse.length > 1 &&
      isBulkCreationSupportedForProductType(type);
    const preselectedTeeth = isBulkCreation
      ? [...new Set(teethToUse)].sort((first, second) => first - second)
      : [];
    const teethToCreate = isBulkCreation
      ? preselectedTeeth.slice(0, 1)
      : teethToUse;

    try {
      const result = await addOrderProducts(
        defaultProducts?.length
          ? defaultProducts
          : [
              {
                type,
                teeth: teethToCreate,
                items: teethToCreate.map(tooth => ({ tooth, type })),
              },
            ]
      ).catch(error => {
        if (
          v1Adapter &&
          (hasGraphQLErrorCode(error, "NON_ADJACENT_TEETH") ||
            hasGraphQLErrorCode(error, "MULTI_TEETH_NOT_ALLOWED"))
        ) {
          return addOrderProducts([
            {
              type,
              teeth: [],
              items: [],
            },
          ]);
        }

        throw error;
      });

      const products = result.data?.addOrderProducts?.products ?? [];
      const newProduct = products[products.length - 1];

      if (!newProduct) {
        throw new Error("Product crée mais id introuvable dans la réponse");
      }

      goNext({ id: newProduct.id, type }, preselectedTeeth);
    } catch (error) {
      notifications.show({
        title: "Erreur lors de la création du produit",
        message: error instanceof Error ? error.message : "Erreur inconnue",
        color: "red",
      });
    }
  };

  const [openedCategory, setOpenedCategory] = useState<string | null>(null);
  const openedCategoryData = PRODUCT_CATEGORIES.find(
    category => category.label === openedCategory
  );

  const [query, setQuery] = useState("");
  const isSearching = query.trim().length > 0;
  const searchResults = isSearching
    ? filterCatalog(PRODUCT_CATEGORIES, query)
    : [];

  const selectChoice = (choice: ProductChoice) => {
    // V2 -> V1: types not handled by V2 exit to the V1 funnel
    if (!isV2Product(choice.type)) {
      navigate("/create-order", {
        state: { id: orderId, openAddProduct: true, productType: choice.type },
      });
      return;
    }

    // Edit the product already on this arch rather than create one that replaces it
    const duplicatedProduct = choice.defaultProducts?.length
      ? findProductDuplicatedByDefaults(
          choice.defaultProducts,
          order?.products ?? []
        )
      : undefined;

    if (duplicatedProduct) {
      goNext({ id: duplicatedProduct.id, type: duplicatedProduct.type });
      return;
    }

    void createProduct({
      type: choice.type,
      teethToUse: selectedTeeth,
      defaultProducts: choice?.defaultProducts,
    });
  };

  // The visible products change: forget a selection that may now be hidden
  const changeQuery = (value: string) => {
    setQuery(value);
    setSelectedType(null);

    if (!value.trim()) {
      setOpenedCategory(null);
    }
  };

  return (
    <Group justify="center" pb="s3">
      <LoadingOverlay visible={isAddingProducts} />
      <Box pos="relative" mx="s20">
        <Title variant="display" size="lg" mb="s2">
          Quel{" "}
          <Text span inherit variant="accent">
            type
          </Text>{" "}
          de prothèse ?
        </Title>
        <Text c="ink.6" mb="s8" maw={560}>
          Un type à la fois · vous en ajouterez d&apos;autres juste après.
        </Text>
        <Stack gap="s4">
          <ProductSearch value={query} onChange={changeQuery} />

          {isSearching ? (
            <SearchPanel
              categories={searchResults}
              selectedType={selectedType}
              onSelect={selectChoice}
            />
          ) : openedCategoryData ? (
            <CategoryPanel
              category={openedCategoryData}
              selectedType={selectedType}
              onBack={() => {
                setOpenedCategory(null);
                setSelectedType(null);
              }}
              onSelect={selectChoice}
            />
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="s4">
              {PRODUCT_CATEGORIES.map(category => (
                <ProductCategoryCard
                  key={category.label}
                  label={category.label}
                  isSelected={category.label === openedCategory}
                  description={getCategoryDescription(category)}
                  icon={category.icon}
                  quantity={category.choices.length}
                  onClick={() => setOpenedCategory(category.label)}
                />
              ))}
            </SimpleGrid>
          )}
        </Stack>
      </Box>
    </Group>
  );
};
